import { DateTime } from 'luxon';
import * as queryStringParser from 'query-string';
import semver from 'semver';
import { DeviceShipment, FirmwareType, HwRevision, HwRevisionFirmware } from '@redux/device/deviceTypes';
import { TableCellValueType } from 'types/global';

export const isClient = () => {
  return typeof window !== 'undefined' && window?.__data__;
};

export const isEmpty = (value: any) => value === undefined || value === null || value === '';
export const isListNotEmpty = (value: any) => value !== undefined && value !== null && value.length !== 0;
export const DIGIT_REGEX = /\d/;
export const LOWERCASE_REGEX = /[a-z]/;
export const UPPERCASE_REGEX = /[A-Z]/;
export const SPECIAL_CHAR_REGEX = /[^a-zA-Z\d]/;
export const PASSWORD_REGEX = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z\d])(?=\S+$).{8,}$/;
export const MIN_CHARS = 8;
export const WHITESPACE_REGEX = /\s/;

export type Criterion = {
  id: string;
  label: string;
  test: (pwd: string, repeatPwd?: string) => boolean;
};

export const PASSWORD_CRITERIA: Criterion[] = [
  {
    id: 'length',
    label: `At least ${MIN_CHARS} characters`,
    test: (p) => p.length >= MIN_CHARS
  },
  {
    id: 'upperLower',
    label: 'Upper & lower case letters',
    test: (p) => UPPERCASE_REGEX.test(p) && LOWERCASE_REGEX.test(p)
  },
  {
    id: 'number',
    label: '1 number',
    test: (p) => DIGIT_REGEX.test(p)
  },
  {
    id: 'special',
    label: '1 special character',
    test: (p) => SPECIAL_CHAR_REGEX.test(p)
  }
];

export const CONFIRM_PASSWORD_CRITERIA: Criterion[] = [
  {
    id: 'repeat',
    label: 'Passwords match',
    test: (pwd, repeatPwd) => !!pwd && !!repeatPwd && pwd === repeatPwd
  }
];

export function isPasswordValid(value: string): boolean {
  return !isEmpty(value) && PASSWORD_REGEX.test(value);
}

export function isEmailValid(value: string): boolean {
  if (isEmpty(value)) {
    return false;
  }
  if (!value.includes('@') && !value.includes('.')) {
    return false;
  }
  // Simple, efficient email validation: one @, at least one dot in domain, no spaces
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function isPhoneNumberValid(value: string): boolean {
  return !isEmpty(value) && /^\+\d{1}\s\(\d{3}\)\s\d{3}-\d{4}$/.test(value); // Example: +1 (123) 456-7890
}

export function isBirthDateValid(value: string): boolean {
  if (isEmpty(value)) {
    return false;
  }
  const date = new Date(value);
  const today = new Date();
  const todayUtc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  return date.getTime() <= todayUtc.getTime();
}

export const birthDateAlertMessage = (date: string): string | undefined => {
  if (!isEmpty(date) && !isBirthDateValid(date)) {
    return 'Birth date cannot be in the future';
  }
};

export function getFormattedPhoneNumber(value: string): string {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');

  // Apply formatting
  let formattedNumber = digitsOnly.replace(/^(\+?1)?(\d{0,3})?(\d{0,3})?(\d{0,4})?/, (match, p1, p2, p3, p4) => {
    let formatted = '+1';
    if (p2) formatted += ` (${p2}`;
    if (p3) formatted += `) ${p3}`;
    if (p4) formatted += `-${p4}`;
    return formatted;
  });

  return formattedNumber.slice(0, 17); // Example: +1 (300) 400-5000
}

export function getCleanPhoneNumber(value: string): string {
  return value.replace(/\D/g, ''); // Example: +11234567890
}

export const passwordAlertMessage = (password: string): string | undefined => {
  if (!isEmpty(password)) {
    if (WHITESPACE_REGEX.test(password)) {
      return 'Password must not contain any spaces';
    }
    if (password.length < MIN_CHARS) {
      return 'Password must be at least 8 characters';
    }
    if (!LOWERCASE_REGEX.test(password)) {
      return 'Password must contain at least one lower case letter';
    }
    if (!UPPERCASE_REGEX.test(password)) {
      return 'Password must contain at least one upper case letter';
    }
    if (!DIGIT_REGEX.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!SPECIAL_CHAR_REGEX.test(password)) {
      return 'Password must contain at least one special character';
    }
  }
};

export const emailAlertMessage = (email: string): string | undefined => {
  if (!isEmpty(email) && !isEmailValid(email)) {
    return 'Not a valid email';
  }
};

export const phoneNumberAlertMessage = (email: string): string | undefined => {
  if (!isEmpty(email) && !isPhoneNumberValid(email)) {
    return 'Not a valid phone number';
  }
};

export const minMaxCheck = (event): string => {
  let { value, min, max } = event.target;
  return minMaxValueCheck(value, min, max);
};

export const minMaxValueCheck = (value: string, min: number, max: number): string => {
  if (checkIsNumber(value)) {
    const newValue = Math.max(Number(min), Math.min(Number(max), Number(value)));
    return value.length === 2 && newValue < 10 ? `0${newValue}` : `${newValue}`;
  }
  return value;
};

export const checkIsNumber = (value: Nullable<string>): boolean => {
  return !!value && !isNaN(Number(value));
};

export const getQueryParamObject = (queryString: Nullable<string>): any => {
  if (queryString) {
    return queryStringParser.parse(queryString);
  }
  return {};
};

export const goTo = (url: string, navigate: any, state?: Nullable<any>) => {
  return () => {
    navigate(url, { state });
  };
};

export const getKokoMapUrl = (userId: Nullable<string>, timeZone: Nullable<string>, fromDevice: boolean): Nullable<string> => {
  if (timeZone) {
    const currentHour = DateTime.now().setZone(timeZone).get('hour');
    let startDate = DateTime.now().setZone(timeZone).set({ hour: 12, minute: 0, second: 0, millisecond: 0 });
    if (currentHour < 18) {
      startDate = startDate.minus({ hours: 24 });
    }
    const endDate = startDate.plus({ hours: 24 });
    const url = `/user/${userId}/kokoMap?startTs=${startDate.toISO()}&endTs=${endDate.toISO()}` + (fromDevice ? '&fromDevice=true' : '');
    return url;
  }
};

export const goToKokoMap = (userId: string, timeZone: Nullable<string>, navigate: any, fromDevice: boolean) => {
  const url = getKokoMapUrl(userId, timeZone, fromDevice);
  if (url) {
    navigate(url);
  }
};

export const ellipsify = (text: string, maxLength: number): string => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength - 3) + '...';
  }
  return text;
};

export const mapsAreEqual = (map1: Map<any, any>, map2: Map<any, any>): boolean => {
  let testVal;
  if (map1.size !== map2.size) {
    return false;
  }
  for (let entry of Array.from(map1.entries())) {
    let key = entry[0];
    let val = entry[1];
    testVal = map2.get(key);
    if (testVal !== val || (testVal === undefined && !map2.has(key))) {
      return false;
    }
  }
  return true;
};

export const arraysAreEqual = (arr1: any[], arr2: any[]): boolean => arr1?.length == arr2?.length && arr1.every((element, index) => element === arr2[index]);

export const areEqualBlank = (s1?: Nullable<string>, s2?: Nullable<string>): boolean => s1 === s2;

export const areEqualZero = (n1?: Nullable<number>, n2?: Nullable<number>): boolean => (n1 || 0) === (n2 || 0);

export const extractRouted = (location: any): string | undefined => {
  return extractRouteParam('routed', location);
};

export const extractRouteParam = (param: string, location: any): any | undefined => {
  const locationState = location?.state;
  return (locationState as any)?.[param];
};

export const reduxActionPrefix = (reduxAction: string) => reduxAction.substring(0, reduxAction.lastIndexOf('-'));

export const deviceShipmentSorter = (a: DeviceShipment, b: DeviceShipment): number => {
  const receivedA: Nullable<string> = a?.deviceReceivedTs;
  const receivedB: Nullable<string> = b?.deviceReceivedTs;
  if (receivedA && !receivedB) {
    return -1;
  } else if (receivedB && !receivedA) {
    return 1;
  } else if (receivedA !== receivedB && receivedA && receivedB) {
    return receivedA?.localeCompare(receivedB);
  } else {
    const shippedA: Nullable<string> = a?.deviceShippedTs;
    const shippedB: Nullable<string> = b?.deviceShippedTs;
    if (shippedA && !shippedB) {
      return -1;
    } else if (shippedB && !shippedA) {
      return 1;
    } else if (shippedA === shippedB) {
      return 0;
    }
    return shippedB || shippedB === '0' ? shippedA?.localeCompare(shippedB) || 0 : 0;
  }
};

export const getMaxKeyOfMaps = (...maps: Map<number, any>[]): Nullable<number> => {
  let maxKey: number | undefined = undefined;
  for (const map of maps) {
    for (const key of Array.from(map.keys())) {
      if (maxKey === undefined || key > maxKey) {
        maxKey = key;
      }
    }
  }
  return maxKey;
};

export const getFirmwareTypeForHwRevision = (hwRevision: Nullable<HwRevision>, hwRevisionFirmware: HwRevisionFirmware[]): FirmwareType => {
  return hwRevisionFirmware.find((x) => x.hwRevision === hwRevision)?.firmwareType || FirmwareType.UNKNOWN;
};

export const transferKgToLbs = (weight: number) => {
  return weight * 2.205;
};

export const getInitials = (fullName: string): string => {
  return fullName
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join(' ');
};

export const mergeArraysToMap = (keys: string[], values: string[]): { [key: string]: string } => {
  const map: { [key: string]: string } = keys.reduce((map: { [key: string]: string }, key, index) => {
    map[key] = values[index];
    return map;
  }, {});

  return map;
};

export const getEnumKey = <TEnumKey extends string, TEnumValue extends string | number>(enumInput: { [key in TEnumKey]: TEnumValue }, value: TEnumValue): TEnumKey | undefined => {
  return Object.keys(enumInput).find((key) => enumInput[key] === value) as TEnumKey | undefined;
};

export const getDisplayValueOrDefault = (value: TableCellValueType | undefined, defaultValue = '-', postfix = ''): string => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  if (typeof value === 'string') {
    return value + postfix;
  }
  return '' + getFixedPointDisplay(value) + postfix;
};

export const getFixedPointDisplay = (value?: Nullable<number>, defaultForNull: string = '-', defaultFixNum: number = 1): string | number => {
  if (typeof value !== 'number') {
    return defaultForNull;
  }
  if (Number.isInteger(value)) {
    return value;
  } else {
    return Number(value.toFixed(defaultFixNum));
  }
};

export const properCase = (value?: string, defaultValue: string = '-'): string => {
  if (!value) {
    return defaultValue;
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const compareSemVersion = (v1: string, v2: string): number => {
  try {
    // 0.6.4-20210709_234119
    const cleanV1 = v1.split('-')[0];
    const cleanV2 = v2.split('-')[0];
    return semver.compare(cleanV1, cleanV2);
  } catch (e) {
    console.error('Error comparing versions', e);
    return -1;
  }
};

export const trimObjectValues = (obj: any) => Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]));

/**
 * Truncates instruction text to a specified maximum length
 * @param instruction - The instruction text to truncate (can be null or undefined)
 * @param maxLength - Maximum length before truncation (default: 80)
 * @returns Truncated string with ellipsis if truncated, or empty string for null/undefined
 */
export const truncateInstruction = (instruction: string | null | undefined, maxLength: number = 80): string => {
  if (!instruction) return '';
  return instruction.length > maxLength ? `${instruction.substring(0, maxLength)}...` : instruction;
};
